import { Link } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight } from "lucide-react";
import { useStore, formatINR } from "@/store/StoreContext";
import Seo from "@/components/store/Seo";
import { imgW, imgFallback } from "@/utils/img";

export default function Cart() {
  const { cart, updateCartItem, removeCartItem, settings } = useStore();
  const items = cart.items || [];
  const totals = cart.totals;
  const threshold = settings?.shipping?.free_threshold || 1999;
  const away = Math.max(0, threshold - (totals?.subtotal || 0));
  const offers = settings?.prepaid_offers || [];
  const sub = totals?.subtotal || 0;
  const qualified = offers.filter((o) => sub >= (o.min_order || 0)).sort((a, b) => (b.min_order || 0) - (a.min_order || 0))[0];
  const upcoming = !qualified && offers.length ? [...offers].sort((a, b) => (a.min_order || 0) - (b.min_order || 0))[0] : null;
  const offer = qualified || upcoming;
  const offerDesc = (o) => [
    o.discount_value > 0 ? (o.discount_type === "percent" ? `${o.discount_value}% extra off` : `₹${o.discount_value} extra off`) : null,
    o.shipping_discount_type === "free" ? "FREE shipping" : null,
    o.shipping_discount_type === "percent" && o.shipping_discount_value > 0 ? `${o.shipping_discount_value}% off shipping` : null,
    o.shipping_discount_type === "flat" && o.shipping_discount_value > 0 ? `₹${o.shipping_discount_value} off shipping` : null,
  ].filter(Boolean).join(" + ");

  return (
    <div className="tq-container py-8 sm:py-12" data-testid="cart-page">
      <Seo title="Shopping Bag" noindex />
      <h1 className="font-serif text-3xl sm:text-5xl mb-8">Your Bag</h1>
      {items.length === 0 ? (
        <div className="text-center py-20" data-testid="cart-empty-page">
          <ShoppingBag size={48} strokeWidth={1.2} className="mx-auto text-[#A8821B] mb-4" />
          <p className="font-serif text-2xl mb-2">Your bag is empty</p>
          <p className="text-sm text-[#666666] mb-8">Discover handcrafted heels, juttis & more.</p>
          <Link to="/collections/new-arrivals" className="tq-btn-dark">Start Shopping</Link>
        </div>
      ) : (
        <div className="grid lg:grid-cols-[1fr_360px] gap-10">
          <ul className="divide-y divide-[#EAEAEA] border-y border-[#EAEAEA]">
            {items.map((it) => (
              <li key={it.variant_id} className="py-5 flex gap-5">
                <Link to={`/products/${it.slug}`} className="shrink-0">
                  <img src={imgW(it.image, 300)} alt={it.name} loading="lazy" onError={imgFallback} className="w-24 h-32 object-cover bg-[#FAFAFA]" />
                </Link>
                <div className="flex-1 min-w-0">
                  <Link to={`/products/${it.slug}`}><p className="font-serif text-lg leading-snug">{it.name}</p></Link>
                  <p className="text-xs text-[#C59B27] mt-1">Size {it.size}{it.color ? ` · ${it.color}` : ""} · SKU {it.sku}</p>
                  {it.stock < it.qty && <p className="text-xs text-[#8B1E21] mt-1">Only {it.stock} available</p>}
                  <div className="flex items-center justify-between mt-3 flex-wrap gap-3">
                    <div className="flex items-center border border-[#EAEAEA]">
                      <button onClick={() => updateCartItem(it.variant_id, it.qty - 1)} data-testid={`cartpage-minus-${it.sku}`} aria-label="Decrease" className="w-9 h-9 flex items-center justify-center hover:bg-[#FAFAFA]"><Minus size={14} /></button>
                      <span className="w-10 text-center text-sm">{it.qty}</span>
                      <button onClick={() => updateCartItem(it.variant_id, it.qty + 1)} data-testid={`cartpage-plus-${it.sku}`} aria-label="Increase" className="w-9 h-9 flex items-center justify-center hover:bg-[#FAFAFA]"><Plus size={14} /></button>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold">{formatINR(it.price * it.qty)}</p>
                      {it.mrp > it.price && <p className="text-xs text-[#8E8E93] line-through">{formatINR(it.mrp * it.qty)}</p>}
                    </div>
                    <button onClick={() => removeCartItem(it.variant_id)} data-testid={`cartpage-remove-${it.sku}`} aria-label="Remove"
                      className="text-[#8E8E93] hover:text-[#8B1E21] transition-colors"><Trash2 size={17} /></button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
          <aside className="tq-card p-6 h-fit sticky top-28" data-testid="cart-summary">
            <p className="font-serif text-2xl mb-5">Order Summary</p>
            {away > 0 ? (
              <p className="text-xs bg-[#FAFAFA] p-3 mb-5">Add <strong>{formatINR(away)}</strong> more for FREE shipping</p>
            ) : (
              <p className="text-xs bg-[#1A3C34]/10 text-[#1A3C34] p-3 mb-5 font-semibold">✓ You've unlocked FREE shipping</p>
            )}
            {offer && (
              <p className="text-xs bg-[#C59B27]/10 text-[#8A6A12] p-3 mb-5" data-testid="prepaid-teaser">
                {qualified
                  ? <>Pay online & get <strong>{offerDesc(qualified)}</strong> on this order</>
                  : <>Add <strong>{formatINR(Math.max(0, (upcoming.min_order || 0) - sub))}</strong> more & pay online to get <strong>{offerDesc(upcoming)}</strong></>}
              </p>
            )}
            <dl className="space-y-3 text-sm">
              <div className="flex justify-between"><dt className="text-[#666666]">Subtotal</dt><dd className="font-medium">{formatINR(totals?.subtotal)}</dd></div>
              {totals?.savings > 0 && <div className="flex justify-between"><dt className="text-[#1A3C34]">You save</dt><dd className="text-[#1A3C34] font-medium">−{formatINR(totals.savings)}</dd></div>}
              <div className="flex justify-between"><dt className="text-[#666666]">Shipping</dt><dd>{totals?.shipping_fee ? formatINR(totals.shipping_fee) : "FREE"}</dd></div>
            </dl>
            <Link to="/checkout" data-testid="cart-checkout-btn" className="tq-btn-dark w-full mt-6">
              Checkout <ArrowRight size={14} />
            </Link>
            <Link to="/collections/new-arrivals" className="block text-center text-xs text-[#C59B27] underline underline-offset-4 mt-4">Continue shopping</Link>
          </aside>
        </div>
      )}
    </div>
  );
}
